import type { LifeSelection } from "./workspace/life";
import {
  buildSelectedTargetFromLifeSelection,
  extractSelectedTargetFromContext,
} from "./workspace/selectedTarget";
import { normalizeRuntimeRouteBillingSource } from "../shared/inference-route-truth";
import type { AiPreference, ConnectedAiProvider } from "../shared/connect-ai";

export type UnifiedChatSurface =
  | "main_chat"
  | "workspace_chat"
  | "workspace_chat_legacy"
  | "contextual_chat"
  | "life_contextual_chat"
  | "sidebar_contextual_chat";

export interface UnifiedChatMessage {
  role: string;
  content: string;
}

export interface UnifiedChatRequestParams {
  model?: string;
  sessionId?: string;
  stream: boolean;
  surface: UnifiedChatSurface;
  messages: UnifiedChatMessage[];
  billingSource?: UnifiedChatRequestBillingSource;
  selections?: LifeSelection[];
  contextSummary?: string;
  uiContext?: Record<string, unknown>;
  provenance?: Record<string, unknown>;
  tenantId?: string;
  requestId?: string;
}

export type UnifiedChatRequestBillingSource = "platform_api" | "user_subscription" | "byo_api";

export interface UnifiedChatInferenceStatusLike {
  mode?: string | null;
  preferredSource?: AiPreference | null;
  connectedApiProviders?: ConnectedAiProvider[] | null;
  routeTruth?: {
    billingSource?: unknown;
  } | null;
}

export function summarizeSelections(selections: LifeSelection[] | undefined): string | undefined {
  if (!Array.isArray(selections) || selections.length === 0) return undefined;
  return selections
    .slice(0, 3)
    .map((selection) => `${selection.title} (${selection.itemType})`)
    .join(", ");
}

export function resolveUnifiedChatRequestBillingSource(
  status: UnifiedChatInferenceStatusLike | null | undefined,
): UnifiedChatRequestBillingSource | undefined {
  if (status?.preferredSource === "workspace_default") return "user_subscription";
  if (status?.preferredSource === "byo_api") return "byo_api";
  if (status?.preferredSource === "byo_subscription") return "user_subscription";

  const routeBillingSource = normalizeRuntimeRouteBillingSource(status?.routeTruth?.billingSource);
  if (routeBillingSource === "byo_subscription") return "user_subscription";
  if (routeBillingSource === "byo_api") return "byo_api";
  if (routeBillingSource === "local_only") return undefined;

  switch (
    String(status?.mode || "")
      .trim()
      .toLowerCase()
  ) {
    case "managed":
      return "user_subscription";
    case "subscription":
      return "user_subscription";
    case "byo_key":
      return "byo_api";
    default:
      return undefined;
  }
}

export function buildUnifiedChatRequest(params: UnifiedChatRequestParams): Record<string, unknown> {
  const selectionSummary = summarizeSelections(params.selections);
  const rawUiContext =
    params.uiContext && typeof params.uiContext === "object" ? params.uiContext : {};
  const { selectedTarget: _ignoredSelectedTarget, ...rawUiContextWithoutSelectedTarget } =
    rawUiContext as Record<string, unknown>;
  // Phase 0A — selection plumbing widening. Order of preference:
  //   1. An explicit selectedTarget on uiContext (caller-supplied, canonical).
  //   2. Auto-derived from the most-recent LifeSelection if none was passed.
  // This unblocks "click a Life item, ask agent about 'this'" without
  // requiring every caller to thread the selection separately.
  let normalizedSelectedTarget = extractSelectedTargetFromContext(rawUiContext);
  if (!normalizedSelectedTarget && Array.isArray(params.selections) && params.selections.length > 0) {
    // Use the LAST selection — chips are append-order, the most-recent click
    // is the one the operator is actively referring to.
    const mostRecent = params.selections[params.selections.length - 1];
    normalizedSelectedTarget = buildSelectedTargetFromLifeSelection(mostRecent);
  }
  const uiContext: Record<string, unknown> = {
    surface: params.surface,
    ...rawUiContextWithoutSelectedTarget,
    ...(normalizedSelectedTarget ? { selectedTarget: normalizedSelectedTarget } : {}),
  };
  if (selectionSummary) {
    uiContext.selectionSummary = selectionSummary;
    uiContext.selectionCount = params.selections?.length ?? 0;
  }
  if (typeof params.contextSummary === "string" && params.contextSummary.trim().length > 0) {
    uiContext.contextSummary = params.contextSummary.trim().slice(0, 800);
  }

  const metadata: Record<string, unknown> = {
    surface: params.surface,
    uiContext,
  };
  if (typeof params.tenantId === "string" && params.tenantId.trim().length > 0) {
    metadata.tenantId = params.tenantId.trim();
  }
  if (typeof params.requestId === "string" && params.requestId.trim().length > 0) {
    metadata.requestId = params.requestId.trim();
  }
  if (params.provenance && typeof params.provenance === "object") {
    metadata.provenance = params.provenance;
  }

  return {
    model:
      typeof params.model === "string" && params.model.trim().length > 0 ? params.model : "auto",
    ...(params.sessionId ? { sessionId: params.sessionId } : {}),
    ...(params.billingSource ? { billingSource: params.billingSource } : {}),
    stream: params.stream,
    messages: params.messages,
    metadata,
  };
}
